import React from 'react'
import { motion, Variants } from 'framer-motion'

import visionBg from '../../../assets/images/ddxpage/ddxbg.webp'

import visionImg from '../../../assets/images/ddxpage/vision.webp'
import problemImg from '../../../assets/images/ddxpage/problem.webp'
import solutionImg from '../../../assets/images/ddxpage/solution.webp'

const visionData = [
  {
    heading: 'The Vision',
    title: 'Healthcare, simplified through intelligence.',
    para:
      'DDX is built to eliminate uncertainty in early stage diagnosis. By combining AI with intuitive design, the platform empowers users to understand symptoms instantly & take informed action— without friction, delay, or confusion.',
    image: visionImg,
  },
  {
    heading: 'The Problem',
    title: 'Delayed diagnosis. Limited access. Unclear next steps.',
    para:
      'Users often rely on guesswork or fragmented information when symptoms appear. Traditional healthcare systems are reactive, time-consuming, & inaccessible for many creating a critical gap in early intervention.',
    image: problemImg,
  },
  {
    heading: 'The Solution',
    title: 'An intelligent, guided diagnostic journey.',
    para:
      'We designed a seamless AI-powered experience that listens, analyzes, and responds in real time. DDX transforms complex medical reasoning into a simple, conversational flow—making healthcare feel approachable and immediate.',
    image: solutionImg,
  },
]

const leftVariant: Variants = {
  hidden: {
    opacity: 0,
    x: -120,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.9,
      ease: [0.25, 0.1, 0.25, 1] as const,
    },
  },
}

const rightVariant: Variants = {
  hidden: {
    opacity: 0,
    x: 120,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.9,
      ease: [0.25, 0.1, 0.25, 1] as const,
    },
  },
}

const imageVariant: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.7,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 1,
      ease: [0.25, 0.1, 0.25, 1] as const,
    },
  },
}

const Vision = () => {
  return (
    <section
      className="relative w-full overflow-hidden bg-cover bg-center bg-no-repeat bg-white"
      style={{
        backgroundImage: `url(${visionBg})`,
      }}
    >
      {/* Top Left Glow */}
      <div
        className="pointer-events-none absolute left-10 top-[15%] z-0 h-[400px] w-[450px] rounded-full blur-[200px] lg:left-40 lg:h-[500px] lg:w-[600px]"
        style={{
          background:
            'linear-gradient(293.95deg,#2B0946 0%,#5E0F9B 100%)',
          opacity: 0.7,
          transform: 'translate(-60%,-40%)',
        }}
      />

      {/* Bottom Right Glow */}
      <div
        className="pointer-events-none absolute bottom-[30%] right-[10px] z-0 h-[400px] w-[450px] rounded-full blur-[200px] lg:right-40 lg:h-[500px] lg:w-[600px]"
        style={{
          background:
            'linear-gradient(293.95deg,#2B0946 0%,#5E0F9B 100%)',
          opacity: 0.7,
          transform: 'translate(60%,40%)',
        }}
      />

      <div className="relative z-10 mx-auto max-w-[1900px] px-5 pb-5 md:px-10 md:pt-15 lg:pt-25 xl:px-20">
        {visionData.map((item, index) => {
          const right = index % 2 === 0
          const imageLeft = index % 2 === 0

          return (
            <motion.div
              key={item.heading}
              className={`mb-10 flex md:mb-20 lg:mb-30 xl:mb-40 ${
                right ? 'justify-end' : 'justify-start'
              }`}
              variants={right ? rightVariant : leftVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.25 }}
            >
              <div className="relative mt-5 mb-5 w-full lg:w-[75%] xl:w-[72%]">

                {/* Heading */}
                <div
                  className={`w-full max-w-full text-center md:max-w-[75%] ${
                    right
                      ? 'md:ml-auto md:pl-[1.5%]'
                      : 'md:mr-auto md:pl-[2.5%]'
                  }`}
                >
                  <h2
                    className="mb-5 text-center font-gilroy-extrabold text-3xl uppercase text-transparent md:text-3xl lg:mb-3 lg:text-left lg:text-4xl xl:mb-6"
                    style={{
                      background:
                        'linear-gradient(293.95deg,#2B0946 0%,#5E0F9B 100%)',
                      WebkitBackgroundClip: 'text',
                    }}
                  >
                    {item.heading}
                  </h2>
                </div>

                {/* Card */}
                <div
                  className="relative overflow-visible border border-[#5E0F9B]/20 md:px-5 md:py-5 xl:px-8 xl:py-8"
                  style={{
                    background: 'rgba(255,255,255,0.92)',
                    backdropFilter: 'blur(4px)',
                    WebkitBackdropFilter: 'blur(4px)',
                    boxShadow: '0px 10px 50px rgba(43,9,70,.08)',
                  }}
                >

                  {/* Floating Image */}
                  <motion.img
  src={item.image}
  alt={item.heading}
  variants={imageVariant}
  initial="hidden"
  whileInView="visible"
  viewport={{ once: false, amount: 0.3 }}

  // CONTINUOUS ROTATION
  animate={{
    rotate: imageLeft ? [-7, 0, 7, 0] : [7, 0, -7, 0],
  }}

  transition={{
    rotate: {
      duration: 4,
      repeat: Infinity,
      repeatType: 'mirror',
      ease: 'easeIn',
    },
  }}

  className={`
    relative
    mx-auto
    block
    w-[250px]

    md:absolute
    md:top-1/2
    md:z-20
    md:w-[280px]
    md:-translate-y-1/2

    lg:w-[340px]

    xl:w-[400px]

    2xl:w-[420px]

    ${
      imageLeft
        ? '-left-[1px] md:-left-[70px] lg:-left-[110px]'
        : '-right-[1px] md:-right-[70px] lg:-right-[110px]'
    }
  `}
/>

                  {/* Content */}
                  <div
                    className={`relative z-10 w-full max-w-full text-center md:max-w-[75%] ${
                      right ? 'md:ml-auto' : 'md:mr-auto'
                    }`}
                  >
                    <h3 className="text-center font-gilroy-bold text-2xl text-[#0A0111] md:text-left md:text-2xl xl:text-3xl">
                      {item.title}
                    </h3>

                    <p className="px-3 text-center font-gilroy-medium text-base leading-relaxed text-[#0A0111] md:mt-3 md:px-0 md:text-left md:text-lg xl:mt-5 xl:text-xl 2xl:text-2xl">
                      {item.para}
                    </p>
                  </div>

                </div>
              </div>
            </motion.div>
          )
        })}
      </div>
    </section>
  )
}

export default Vision